import React from "react";
import { css, styled } from "@sourceful/shared-components";
import Flickity, { FlickityOptions } from "react-flickity-component";
import "flickity/dist/flickity.min.css";
import toRem from "@styles/postprocess/conversion/to-rem";
import { getSanityImgUrl } from "@lib/sanity";
import { BasicImage } from "@lib/utils/sanityImageToBasicImage";
import { FlickityCarouselEvents } from "./GalleryWrapper";
import Image from "next/image";
import { YouTubeVideo } from "./YouTubeVideo";

export const ProductImageStyles = css({
  width: "100%",
  height: "auto",
});

export const ProductImageFigure = styled("figure", {
  width: "100%",
  height: "auto",
});

export const Caption = styled("figcaption", {
  padding: toRem(30),
  textAlign: "center",
  fontSize: "$p2",
});

interface ImageGalleryProps {
  images: BasicImage[];
  flicketyOptions?: FlickityOptions;
  showCaptions?: boolean;
  registerGallery: (c: Flickity) => void;
  flicketyEvents?: FlickityCarouselEvents;
}

interface ImageGalleryState {
  isReady: boolean;
}

const IMAGE_DIMENSION = 600;

class ImageGallery extends React.Component<ImageGalleryProps, ImageGalleryState> {
  state = {
    isReady: false,
    currentIndex: 0,
  };

  render() {
    const { registerGallery, images, flicketyOptions, showCaptions, flicketyEvents } = this.props;

    return (
      <Flickity
        static
        flickityRef={registerGallery}
        className="main-image-carousel"
        options={{
          initialIndex: 0,
          pageDots: false,
          resize: true,
          prevNextButtons: false,
          wrapAround: false,
          // @ts-ignore
          on: {
            ready: () => {
              if (flicketyEvents?.onReady) {
                flicketyEvents.onReady();
              }
            },
            change: (index: number) => {
              if (flicketyEvents?.onChange) {
                flicketyEvents.onChange(index);
                this.setState(s => ({
                  ...s,
                  currentIndex: index,
                }));
              }
            },
          },
          arrowShape: {
            x0: 10,
            x1: 60,
            y1: 50,
            x2: 70,
            y2: 50,
            x3: 20,
          },
          ...flicketyOptions,
        }}
      >
        {images.map((image, index) => {
          if (!!image.youtubeId) {
            return (
              <YouTubeVideo
                key={image.assetRef}
                youtubeId={image.youtubeId}
                width={IMAGE_DIMENSION}
                height={IMAGE_DIMENSION}
                autoplay={image.autoplay}
                showPlayer={this.state.currentIndex === index}
              />
            );
          }

          return (
            <ProductImageFigure key={image.assetRef}>
              <Image
                loading={index === 0 ? "eager" : "lazy"}
                priority={index === 0}
                src={getSanityImgUrl(image.assetRef, baseTransforms => baseTransforms.width(600))}
                alt={image?.altText || image?.description || ""}
                width={IMAGE_DIMENSION}
                height={IMAGE_DIMENSION}
                className={`${ProductImageStyles()} product-image`}
                unoptimized
              />
              {showCaptions && image?.description && <Caption>{image?.description}</Caption>}
            </ProductImageFigure>
          );
        })}
      </Flickity>
    );
  }
}

export default ImageGallery;
